import React, { useState } from 'react';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { CardToTokenFlow } from './CardToTokenFlow';
import { isCardToTokenEnabled } from '@/config/cardToTokenConfig';
import { 
  CreditCard, 
  AlertCircle,
  Info
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@vaiu/ui/dialog';

interface PaymentMethodATMProps {
  amount: number;
  currency: string;
  onSuccess: (tokenId: string, authCode: string) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * PaymentMethodATM - ATM withdrawal method component
 * Allows users to tap their bank debit card via NFC to withdraw from their bank account
 * Uses Base24 integration for virtual digital ATM functionality
 */
export const PaymentMethodATM: React.FC<PaymentMethodATMProps> = ({
  amount,
  currency,
  onSuccess,
  onError,
  onCancel,
  disabled = false,
  className
}) => {
  const [showDialog, setShowDialog] = useState(false);

  if (!isCardToTokenEnabled()) {
    return null;
  }

  const handleOpen = () => {
    if (!disabled) {
      setShowDialog(true);
    }
  };

  const handleSuccess = (tokenId: string, authCode: string) => {
    setShowDialog(false);
    onSuccess(tokenId, authCode);
  };

  const handleCancel = () => {
    setShowDialog(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleError = (error: string) => {
    setShowDialog(false);
    if (onError) {
      onError(error);
    }
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full"
      >
        <CreditCard className="w-4 h-4 mr-2" />
        Tap Bank Card (ATM Withdrawal)
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>ATM Withdrawal</DialogTitle>
            <DialogDescription>
              Tap your bank debit card to withdraw funds from your bank account
            </DialogDescription>
          </DialogHeader>
          
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertDescription>
              This is a virtual digital ATM. Tap your bank debit card to withdraw {amount.toFixed(2)} {currency} from your bank account.
            </AlertDescription>
          </Alert>

          <CardToTokenFlow
            amount={amount}
            currency={currency}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            onError={handleError}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

